import React, { Component } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import * as PropTypes from 'prop-types';

import SalaryTable from 'layouts/salaryJobPage/child/salaryTable';
import {
  buildCppSubpageUrl,
  formatCurrency,
  formatCurrencyWithTwoDecimals,
  yearlySalaryToHourlyRate,
} from 'utils';
import * as CONSTANTS from 'utils/constant';

import NewSalaryChartComponent from '../../components/newSalaryChartComponent';
import { CHART_TYPES, ENTITY } from '../../components/newSalaryChartComponent/chartUtils';
import ZpFilter from '../../components/zpFilter';
import { CAROUSEL_FILTERS, FILTER_PARAMS } from '../../components/zpFilter/filterUtils';
import CommonService from '../../services/commonService';

const ARTICLE_POST_SUBTYPE = 'new-salaries-by-company';

const customColumnConfig = {
  rank: {
    columnName: 'Rank',
  },
  companyNameCPS: {
    columnName: 'Company',
    linkUrl: 'companyLink',
    isLink: 'toBeIndexed',
    linkTitle: 'companyNameColLinkTitle',
  },
  salary: {
    columnName: `Average Salary`,
    customSortByField: 'median',
    columnNameMobile: `Avg. Salary`,
    linkTitle: 'salaryColLinkTitle',
  },
  formattedHourlyRate: {
    columnName: 'Hourly Rate',
    customSortByField: 'hourlyRate',
  },
};

const propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  articleContent: PropTypes.any,
  columnConfig: PropTypes.object,
  subtype: PropTypes.string,
  basicData: PropTypes.objectOf(PropTypes.any),
  articleData: PropTypes.arrayOf(PropTypes.object),
  asTable: PropTypes.bool,
  addFilterLinkToUrl: PropTypes.func,
  withoutJobTitle: PropTypes.bool,
  noTitle: PropTypes.bool,
  defaultRowsDisplay: PropTypes.number,
  showMoreText: PropTypes.string
};

const defaultProps = {
  basicData: {
    jobTitle: '',
    jobTitlePlural: '',
    salaryByCompany: [],
  },
  articleData: [
    {
      post_subtype: ARTICLE_POST_SUBTYPE,
      content: '',
    },
  ],
  withoutJobTitle: false,
  noTitle: false,
};
class SalariesByCompany extends Component {
  constructor(props) {
    super(props);

    const { basicData, articleData = [], subtype, articleContent = null } = this.props;

    const articleSubtype = subtype || ARTICLE_POST_SUBTYPE;

    const articleSalaryByCompanyData = articleData.find(
      (item) => item.post_subtype === articleSubtype
    );

    const { jobTitle = '', salaryByCompany = [] } = basicData;

    const showSalariesByCompanyTable = salaryByCompany && salaryByCompany.length > 0;

    const articleDataContent =
      (articleSalaryByCompanyData && articleSalaryByCompanyData.content) || articleContent;

    const tableSet = salaryByCompany.map((row) => {
      return {
        ...row,
        medianDisplay: formatCurrency(row.median),
        hourlyRateDisplay: formatCurrencyWithTwoDecimals(row.hourlyRate),
        companyNameColLinkTitle: `Working At ${row.companyNameCPS}`,
        salaryColLinkTitle: `${row.companyNameCPS} ${jobTitle} Salary`,
      };
    });

    this.state = {
      jobTitle,
      articleDataContent,
      filteredCompanies: [],
      showSalariesByCompanyTable,
      tableSet,
    };
  }

  handleFilterClear = () => {
    this.setState({
      filteredCompanies: [],
    });
  };

  updateSalaries = (_, data) => {
    const { withoutJobTitle, basicData } = this.props;
    const { jobTitle } = basicData;
    if (withoutJobTitle) {
      const payload = data.map((item) => {
        return {
          companyID: item.value.companyID,
          percentile: [25, 75],
        };
      });

      CommonService.fetchSalariesByCompany(payload)
        .then((responseSalaryItems) => {
          let filteredCompanies = responseSalaryItems
            ? responseSalaryItems.map((company, index) => {
                const hourlyRate = yearlySalaryToHourlyRate(company.median);
                return {
                  ...company,
                  rank: index + 1,
                  salary: formatCurrency(company.median),
                  hourlyRate,
                  formattedHourlyRate: formatCurrencyWithTwoDecimals(hourlyRate),
                  companyLink: buildCppSubpageUrl(
                    company?.companyLink,
                    '',
                    CONSTANTS.CPP_SUBPAGES.JOBS
                  ),
                };
              })
            : [];
          this.setState({ filteredCompanies });
        })
        .catch((error) => {
          console.error('Error occurred while fetching salaries for companies:', error);
        });
    } else {
      const companyIdList = [];
      const payload = Object.assign({ title: this.state.jobTitle }, { company: [] });

      data.forEach(({ value }) => {
        payload.company.push(value.companyID);
        companyIdList.push(value.companyID);
      });

      if (!payload.company.length) {
        return this.handleFilterClear();
      }

      CommonService.fetchSalariesByTitleAndCompany(payload)
        .then((fetchedCompanies) => {
          const filteredCompanies = fetchedCompanies.map((row) => {
            return {
              ...row,
              companyNameColLinkTitle: `Working At ${row.companyNameCPS}`,
              salaryColLinkTitle: `${row.companyNameCPS} ${jobTitle} Salary`,
              jobCountColLinkTitle: `${row.companyNameCPS} ${jobTitle} Jobs`,
            };
          });
          this.setState({ filteredCompanies });
        })
        .catch((error) => {
          console.error('Error occurred while fetching salaries for companies:', error);
        });
    }
  };

  render() {
    const { showSalariesByCompanyTable, articleDataContent, filteredCompanies, tableSet } =
      this.state;

    const { basicData, asTable, title, subtitle, columnConfig, withoutJobTitle, noTitle, defaultRowsDisplay, showMoreText } =
      this.props;

    if (!showSalariesByCompanyTable) {
      return '';
    }

    const tableHeaders = () => {
      return (
        <thead>
          <tr className="z-px-20 z-py-10 d-flex w-100">
            <th className="d-none d-md-block justify-content-center align-self-center col-md-1 px-0">
              <span className="font-weight-600 greyLabel font-14">Rank</span>
            </th>
            <th className="col-7 col-md-6 justify-content-center align-self-center px-0">
              <span className="font-weight-600 greyLabel font-14">Company</span>
            </th>
            <th className="d-none d-md-block justify-content-center align-self-center col-md-2 px-0">
              <span className="font-weight-600 greyLabel font-14">Zippia Score</span>
            </th>
            <th className="d-none d-md-block col-5 col-md-3 justify-content-center align-self-center px-0">
              <span className="font-weight-600 greyLabel font-14">{`Average ${basicData.jobTitle} Salary`}</span>
            </th>
            <th className="d-block d-md-none col-5 col-md-3 justify-content-center align-self-center px-0">
              <span className="font-weight-600 greyLabel font-14">Average Salary</span>
            </th>
          </tr>
        </thead>
      );
    };

    return (
      <>
        <div className="w-730" id="highest-paying-companies">
          {!noTitle && (
            <h2 className="brandonH2 mb-5">
              {title || `Which companies pay ${basicData.jobTitlePlural?.toLowerCase?.()} the most?`}
            </h2>
          )}

          {articleDataContent && (
            <div
              className="mb-5"
              dangerouslySetInnerHTML={{
                __html: articleDataContent,
              }}
            />
          )}
          <div
            className={cn({
              'z-mb-24': asTable && subtitle,
              'mb-36': !asTable,
            })}
          >
            <ZpFilter
              {...CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY]}
              onApply={this.updateSalaries}
              onClear={this.handleFilterClear}
            />
          </div>

          {asTable && subtitle && <h3 className="zp-title-h3 z-mb-24 text-capitalize">{subtitle}</h3>}

          {asTable && (
            <div className="salary-table-section mt-4">
              <SalaryTable
                hideRows
                customColumnConfig={
                  withoutJobTitle
                    ? columnConfig || customColumnConfig
                    : !filteredCompanies.length
                    ? columnConfig || customColumnConfig
                    : customColumnConfig
                }
                defaultRowsDisplay={defaultRowsDisplay || tableSet.length}
                showMoreText={showMoreText}
                columnWidthBalanced={false}
                tableDataSet={!filteredCompanies.length ? tableSet : filteredCompanies}
              />
            </div>
          )}
        </div>

        {!asTable && (
          <NewSalaryChartComponent
            chartType={CHART_TYPES.TYPE_B}
            filteredBy={ENTITY.COMPANY}
            filteredItems={filteredCompanies}
            salaryChartItems={basicData.salaryByCompany}
            showSignUp
            tableHeaders={tableHeaders}
            tableHeaderTitleContent={
              <h3 className="zp-title-h3 text-capitalize mb-4">
                Highest Paying Companies For {basicData.jobTitlePlural}
              </h3>
            }
          />
        )}
      </>
    );
  }
}

SalariesByCompany.propTypes = propTypes;
SalariesByCompany.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  data: state.searchFilter.data,
});

export default connect(mapStateToProps)(SalariesByCompany);
